import GroupHead from "@presentation/components/groups/TotalGroupsHead";
import GroupRow from "@presentation/components/groups/TotalGroupsRow";
import { GroupHeadCellData } from "@domain/Interfaces/HeadCellData.d";
import Order from "@domain/Interfaces/Order.d";
import { Box, TableContainer, Table, TableBody } from "@mui/material";
import TotalTablePagination from "@presentation/components/TotalTablePagination";
import React, { useEffect, useState } from "react";
import { GroupHeadCell } from "@domain/Interfaces/HeadCell.d";
import { Group } from "@domain/entities/Groups";
import {
  calculateNumberOfEmptyRows,
  defaultTableSx,
  getComparator,
  getPageNumber,
  stableSort,
} from "@presentation/components/table/utils";
import EmptyRows from "@presentation/components/table/EmptyRow";
import useStoredPagination from "@presentation/utils/StoredPagination";
import useStoredRowsPerPage from "@presentation/utils/StoredRowsPerPage";

type GroupTableProps = {
  headCells: GroupHeadCell[];
  rows: Group[];
};

const TotalGroupsTable = ({ headCells, rows }: GroupTableProps): React.JSX.Element => {
  const [storedPagination, setStoredPagination] = useStoredPagination("groups", {
    page: 0,
    order: "asc",
    orderBy: "groupName",
  });
  const [rowsPerPage, setRowsPerPage] = useStoredRowsPerPage(10);
  const [pageNumber, setPageNumber] = useState<number>(getPageNumber(rowsPerPage, rows.length));
  if (storedPagination.page > pageNumber) {
    setStoredPagination({ ...storedPagination, page: 0 });
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof GroupHeadCellData): void => {
    const isAsc = storedPagination.orderBy === property && storedPagination.order === "asc";
    setStoredPagination({ ...storedPagination, order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setStoredPagination({ ...storedPagination, page: newPage - 1 });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value));
    setStoredPagination({ ...storedPagination, page: 0 });
  };

  useEffect(() => {
    setPageNumber(getPageNumber(rowsPerPage, rows.length));
  }, [rowsPerPage, rows]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = calculateNumberOfEmptyRows(storedPagination.page, rowsPerPage, rows.length);

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer>
        <Table sx={defaultTableSx} aria-labelledby="tableTitle">
          <GroupHead
            order={storedPagination.order as Order}
            orderBy={storedPagination.orderBy as keyof GroupHeadCellData}
            onRequestSort={handleRequestSort}
            headCells={headCells}
          />
          <TableBody>
            {stableSort(
              rows,
              getComparator(storedPagination.order as Order, storedPagination.orderBy as keyof GroupHeadCellData)
            )
              .slice(storedPagination.page * rowsPerPage, storedPagination.page * rowsPerPage + rowsPerPage)
              .map((row) => {
                return (
                  <GroupRow
                    key={`group-list-${row.id}`}
                    row={row}
                    highlight={row.scopesLength === 0 || row.usersLength === 0}
                  />
                );
              })}
            <EmptyRows emptyNumber={emptyRows} />
          </TableBody>
        </Table>
      </TableContainer>
      <TotalTablePagination
        page={storedPagination.page}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        nbItems={rows.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default TotalGroupsTable;
